import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper,
  IconButton,
  Divider 
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

/**
 * Modern Access Denied page for unauthorized users
 */
const getRoleStr = (val: any): string => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && typeof val.role === 'string') return val.role;
  return '';
};

const AccessDenied = () => {
  const navigate = useNavigate();
  const { user, modulePermissions } = useAppSelector(state => state.auth);
  const rawRole = getRoleStr(modulePermissions?.baseRole)
    || getRoleStr((user as any)?.role)
    || getRoleStr((user as any)?.baseRole)
    || (Array.isArray(user?.roles) ? getRoleStr(user?.roles[0]) : '')
    || 'Member';

  const userRole = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);

  const handleGoHome = () => {
    navigate('/home');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleContactSupport = () => {
    // You can implement contact support logic here
    console.log('Contact support clicked');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1, sm: 2 },
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 4,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: 'linear-gradient(90deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3)',
            }
          }}
        >
          {/* Icon Section */}
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 70, sm: 80, md: 90 },
                height: { xs: 70, sm: 80, md: 90 },
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                mb: { xs: 1, sm: 1.5 },
                position: 'relative',
                boxShadow: '0 15px 30px rgba(238, 90, 36, 0.3)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 5,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              <LockIcon sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, color: 'white', zIndex: 1 }} />
            </Box>
          </Box>

          {/* Main Content */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: '#2d3748',
              mb: { xs: 0.5, sm: 1 },
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' }
            }}
          >
            Access Denied
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: '#4a5568',
              mb: { xs: 0.5, sm: 0.75 },
              fontWeight: 500,
              fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' }
            }}
          >
            Oops! You don't have permission to access this page
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#718096',
              mb: { xs: 1.5, sm: 2 },
              lineHeight: 1.5,
              maxWidth: 480,
              mx: 'auto',
              fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem' }
            }}
          >
            The page you're trying to access requires special permissions that your current account doesn't have. 
            Please contact your administrator or try accessing a different page.
          </Typography>

          {/* User Info */}
          {user && (
            <Box
              sx={{
                backgroundColor: '#f7fafc',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                mb: { xs: 1.5, sm: 2 },
                border: '1px solid #e2e8f0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.25 }}>
                <ShieldIcon sx={{ color: '#667eea', fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ color: '#4a5568', fontWeight: 600, fontSize: '0.8rem' }}>
                  Current User
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#718096', fontSize: '0.8rem' }}>
                Logged in as: <strong>{user.name}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#4a5568', fontSize: '0.8rem', mt: 0.5, fontWeight: 500 }}>
                Role: <strong>{userRole}</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: '#a0aec0', fontSize: '0.7rem' }}>
                If you believe this is an error, please contact support
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: { xs: 1.5, sm: 2 }, opacity: 0.3 }} />

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 1.5 },
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Button
              variant="contained"
              size="medium"
              startIcon={<HomeIcon sx={{ fontSize: 18 }} />}
              onClick={handleGoHome}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                px: { xs: 2.5, sm: 3 },
                py: { xs: 0.75, sm: 1 },
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              Go to Home
            </Button>

            <Button
              variant="outlined"
              size="medium"
              startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
              onClick={handleGoBack}
              sx={{
                borderColor: '#cbd5e0',
                color: '#4a5568',
                borderRadius: 2,
                px: { xs: 2.5, sm: 3 },
                py: { xs: 0.75, sm: 1 },
                fontWeight: 500,
                textTransform: 'none',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                '&:hover': {
                  borderColor: '#a0aec0',
                  backgroundColor: '#f7fafc',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              Go Back
            </Button>

            <IconButton
              onClick={handleContactSupport}
              size="small"
              sx={{
                backgroundColor: '#edf2f7',
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                '&:hover': {
                  backgroundColor: '#e2e8f0',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease'
              }}
              title="Contact Support"
            >
              <ContactSupportIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Additional Help Text */}
          <Typography
            variant="caption"
            sx={{
              color: '#a0aec0',
              mt: { xs: 1.5, sm: 2 },
              display: 'block',
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}
          >
            Need help? Contact your system administrator or 
            <Link 
              to="/help" 
              style={{ 
                color: '#667eea', 
                textDecoration: 'none',
                marginLeft: 4
              }}
            >
              visit our help center
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default AccessDenied;